/**
 * Strategy Builder — Results view.
 *
 * Renders a BacktestReport: headline scorecard, an inline SVG equity/drawdown
 * chart (no chart lib dependency), mandatory tail-dependence, a trades table,
 * optional Tier 3 robustness sections, and warnings.
 */

import type {
  BacktestReport,
  Scorecard,
  EquityPoint,
  TailDependence,
} from '../api/client';

function fmtPct(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return '∞';
  return `${v.toFixed(digits)}%`;
}
function fmtNum(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return '∞';
  return v.toFixed(digits);
}
/** Profit factor: null + infinite flag → ∞; null otherwise → n/a. */
function fmtProfitFactor(s: Scorecard): string {
  if (s.profitFactorInfinite) return '∞';
  if (s.profitFactor === null) return 'n/a';
  return fmtNum(s.profitFactor);
}
function fmt0to1AsPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}
function fmtMoney(v: number): string {
  return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: 'green' | 'red' | 'gray' }) {
  const color = tone === 'green' ? '#16a34a' : tone === 'red' ? '#dc2626' : undefined;
  return (
    <div className="stat-card">
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

function returnTone(v: number): 'green' | 'red' | 'gray' {
  if (v > 0) return 'green';
  if (v < 0) return 'red';
  return 'gray';
}

function ScorecardGrid({ s }: { s: Scorecard }) {
  return (
    <div className="stats-grid">
      <StatCard label="Total Return" value={fmtPct(s.totalReturnPct)} tone={returnTone(s.totalReturnPct)} />
      <StatCard label="CAGR" value={fmtPct(s.cagr)} tone={returnTone(s.cagr)} />
      <StatCard label="Max Drawdown" value={fmtPct(s.maxDrawdownPct)} tone={s.maxDrawdownPct > 0 ? 'red' : 'gray'} />
      <StatCard label="Trades" value={String(s.trades)} />
      <StatCard label="Win Rate" value={fmt0to1AsPct(s.winRate)} />
      <StatCard label="Profit Factor" value={fmtProfitFactor(s)} />
      <StatCard label="Expectancy" value={fmtPct(s.expectancyPct)} tone={returnTone(s.expectancyPct)} />
      <StatCard label="Sharpe" value={s.ratiosNotMeaningful ? '—' : fmtNum(s.sharpe)} />
      <StatCard label="Sortino" value={s.ratiosNotMeaningful ? '—' : fmtNum(s.sortino)} />
      <StatCard label="Calmar" value={s.ratiosNotMeaningful ? '—' : fmtNum(s.calmar)} />
      <StatCard label="Avg Win" value={fmtPct(s.avgWinPct)} tone="green" />
      <StatCard label="Avg Loss" value={fmtPct(s.avgLossPct)} tone="red" />
      <StatCard label="Exposure" value={fmt0to1AsPct(s.exposurePct)} />
      <StatCard label="Longest Losing Streak" value={String(s.longestLosingStreak)} />
    </div>
  );
}

/** Inline SVG equity curve with a drawdown shade. */
function EquityChart({ points }: { points: EquityPoint[] }) {
  if (points.length < 2) {
    return <p className="section-description">Not enough equity points to plot.</p>;
  }
  const W = 720;
  const H = 240;
  const pad = { top: 12, right: 12, bottom: 22, left: 56 };
  const iw = W - pad.left - pad.right;
  const ih = H - pad.top - pad.bottom;

  const equities = points.map((p) => p.equity);
  const minE = Math.min(...equities);
  const maxE = Math.max(...equities);
  const range = maxE - minE || 1;
  const n = points.length;

  const x = (i: number) => pad.left + (i / (n - 1)) * iw;
  const y = (e: number) => pad.top + (1 - (e - minE) / range) * ih;

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.equity).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${x(n - 1).toFixed(1)} ${(pad.top + ih).toFixed(1)} L ${x(0).toFixed(1)} ${(pad.top + ih).toFixed(1)} Z`;

  const first = points[0]!.equity;
  const last = points[n - 1]!.equity;
  const up = last >= first;
  const stroke = up ? '#16a34a' : '#dc2626';
  const fill = up ? 'rgba(22,163,74,0.10)' : 'rgba(220,38,38,0.10)';

  const yTicks = [maxE, minE + range / 2, minE];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Equity curve" style={{ maxWidth: W }}>
      <rect x={pad.left} y={pad.top} width={iw} height={ih} fill="#fafafa" stroke="#e5e7eb" />
      {yTicks.map((t, i) => (
        <g key={i}>
          <line x1={pad.left} x2={pad.left + iw} y1={y(t)} y2={y(t)} stroke="#eee" />
          <text x={pad.left - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill="#888">
            {fmtMoney(t)}
          </text>
        </g>
      ))}
      <path d={areaPath} fill={fill} />
      <path d={linePath} fill="none" stroke={stroke} strokeWidth={1.6} />
      <text x={pad.left} y={H - 6} fontSize="10" fill="#888">
        {new Date(points[0]!.t).toISOString().slice(0, 10)}
      </text>
      <text x={pad.left + iw} y={H - 6} textAnchor="end" fontSize="10" fill="#888">
        {new Date(points[n - 1]!.t).toISOString().slice(0, 10)}
      </text>
    </svg>
  );
}

function TailDependenceCard({ td }: { td: TailDependence }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-title">Tail Dependence (profit concentration)</div>
      <div className="stats-grid">
        <StatCard label="Top-3 Share of Gross Profit" value={fmt0to1AsPct(td.top3ShareOfGrossProfit)} />
        <StatCard label="Profit Factor excl. Top 3" value={fmtNum(td.profitFactorExclTop3)} />
      </div>
      {td.note && <p className="section-description" style={{ marginTop: 8 }}>{td.note}</p>}
      <p className="section-description" style={{ marginTop: 8 }}>
        A high top-3 share means the result leans heavily on a few trades — treat it with caution.
      </p>
    </div>
  );
}

function TradesTable({ report }: { report: BacktestReport }) {
  const trades = report.trades;
  if (trades.length === 0) {
    return <p className="empty-state">No trades were generated for this strategy over the selected window.</p>;
  }
  const shown = trades.slice(0, 200);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Side</th>
            <th>Entry</th>
            <th>Exit</th>
            <th>Qty</th>
            <th>P&amp;L</th>
            <th>Return</th>
            <th>Reason</th>
            <th>Bars</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((t, i) => (
            <tr key={i}>
              <td>{t.symbol}</td>
              <td><span className={`badge ${t.side === 'long' ? 'badge--green' : 'badge--yellow'}`}>{t.side}</span></td>
              <td className="cell-mono">{new Date(t.entryTime).toISOString().slice(0, 10)} @ {fmtNum(t.entryPrice)}</td>
              <td className="cell-mono">{t.exitTime ? `${new Date(t.exitTime).toISOString().slice(0, 10)} @ ${fmtNum(t.exitPrice ?? 0)}` : '—'}</td>
              <td className="cell-mono">{fmtNum(t.qty, 4)}</td>
              <td className="cell-mono" style={{ color: t.pnl >= 0 ? '#16a34a' : '#dc2626' }}>{fmtMoney(t.pnl)}</td>
              <td className="cell-mono" style={{ color: t.returnPct >= 0 ? '#16a34a' : '#dc2626' }}>{fmtPct(t.returnPct)}</td>
              <td>{t.exitReason}</td>
              <td className="cell-mono">{t.barsHeld ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {trades.length > shown.length && (
        <p className="section-description">Showing first {shown.length} of {trades.length} trades.</p>
      )}
    </div>
  );
}

function RobustnessView({ report }: { report: BacktestReport }) {
  const r = report.robustness;
  if (!r) return null;
  return (
    <div style={{ marginTop: 24 }}>
      <h3>Robustness (Tier 3)</h3>

      {r.walk_forward && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="card-title">Walk-Forward — Out-of-Sample is the primary result</div>
          <p className="section-description">{r.walk_forward.note}</p>
          <div className="form-row" style={{ gap: 24 }}>
            <div style={{ flex: 1 }}>
              <div className="detail-label">Out-of-Sample (primary)</div>
              <ScorecardMini s={r.walk_forward.out_of_sample} />
            </div>
            <div style={{ flex: 1 }}>
              <div className="detail-label">In-Sample (fitting window)</div>
              <ScorecardMini s={r.walk_forward.in_sample} />
            </div>
          </div>
          <p className="section-description">{r.walk_forward.windows.length} window(s) evaluated.</p>
        </div>
      )}

      {r.sweep && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="card-title">
            Parameter Sweep <span className="badge badge--yellow">OPTIMIZATION</span>
          </div>
          <p className="section-description">{r.sweep.note}</p>
          <p>Objective: <strong>{r.sweep.objective}</strong> · Selected:{' '}
            <strong>{r.sweep.selected ? JSON.stringify(r.sweep.selected) : 'none'}</strong>
          </p>
          <table className="data-table">
            <thead><tr><th>Params</th><th>Score</th><th>OOS Score</th><th>Trades</th></tr></thead>
            <tbody>
              {r.sweep.rows.map((row, i) => (
                <tr key={i}>
                  <td className="cell-mono">{JSON.stringify(row.params)}</td>
                  <td className="cell-mono">{fmtNum(row.score)}</td>
                  <td className="cell-mono">{row.oos_score === null ? '—' : fmtNum(row.oos_score)}</td>
                  <td className="cell-mono">{row.trades}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {r.monte_carlo && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="card-title">Monte Carlo ({r.monte_carlo.method}, {r.monte_carlo.runs} runs)</div>
          <p className="section-description">{r.monte_carlo.note}</p>
          <table className="data-table">
            <thead><tr><th>Metric</th><th>p05</th><th>p50</th><th>p95</th></tr></thead>
            <tbody>
              {r.monte_carlo.totalReturnOrderInvariant ? (
                <>
                  <tr><td>Total Return %</td><td className="cell-mono" colSpan={3}>{fmtNum(r.monte_carlo.total_return_pct.p50)} <span className="badge badge--gray">order-invariant</span></td></tr>
                  <tr><td>Final Equity</td><td className="cell-mono" colSpan={3}>{fmtMoney(r.monte_carlo.final_equity.p50)} <span className="badge badge--gray">order-invariant</span></td></tr>
                </>
              ) : (
                <>
                  <tr><td>Total Return %</td><td className="cell-mono">{fmtNum(r.monte_carlo.total_return_pct.p05)}</td><td className="cell-mono">{fmtNum(r.monte_carlo.total_return_pct.p50)}</td><td className="cell-mono">{fmtNum(r.monte_carlo.total_return_pct.p95)}</td></tr>
                  <tr><td>Final Equity</td><td className="cell-mono">{fmtMoney(r.monte_carlo.final_equity.p05)}</td><td className="cell-mono">{fmtMoney(r.monte_carlo.final_equity.p50)}</td><td className="cell-mono">{fmtMoney(r.monte_carlo.final_equity.p95)}</td></tr>
                </>
              )}
              <tr><td>Max Drawdown %</td><td className="cell-mono">{fmtNum(r.monte_carlo.max_drawdown_pct.p05)}</td><td className="cell-mono">{fmtNum(r.monte_carlo.max_drawdown_pct.p50)}</td><td className="cell-mono">{fmtNum(r.monte_carlo.max_drawdown_pct.p95)}</td></tr>
            </tbody>
          </table>
        </div>
      )}

      {r.crisis && r.crisis.length > 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="card-title">Crisis Presets</div>
          <table className="data-table">
            <thead><tr><th>Preset</th><th>Window</th><th>Total Return</th><th>Max DD</th><th>Trades</th></tr></thead>
            <tbody>
              {r.crisis.map((c, i) => (
                <tr key={i}>
                  <td>{c.preset}</td>
                  <td className="cell-mono">{c.start.slice(0, 10)} → {c.end.slice(0, 10)}</td>
                  <td className="cell-mono" style={{ color: c.scorecard.totalReturnPct >= 0 ? '#16a34a' : '#dc2626' }}>{fmtPct(c.scorecard.totalReturnPct)}</td>
                  <td className="cell-mono">{fmtPct(c.scorecard.maxDrawdownPct)}</td>
                  <td className="cell-mono">{c.trades}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScorecardMini({ s }: { s: Scorecard }) {
  return (
    <div className="stats-grid">
      <StatCard label="Total Return" value={fmtPct(s.totalReturnPct)} tone={returnTone(s.totalReturnPct)} />
      <StatCard label="Max DD" value={fmtPct(s.maxDrawdownPct)} tone={s.maxDrawdownPct > 0 ? 'red' : 'gray'} />
      <StatCard label="Sharpe" value={fmtNum(s.sharpe)} />
      <StatCard label="Trades" value={String(s.trades)} />
    </div>
  );
}

export function ResultsView({ report }: { report: BacktestReport }) {
  return (
    <div>
      <h3>Results</h3>

      {report.warnings.length > 0 && (
        <div className="card" style={{ marginTop: 12, borderLeft: '4px solid #f59e0b' }}>
          <div className="card-title">Warnings</div>
          <ul className="modal-list">
            {report.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <ScorecardGrid s={report.summary} />
      {report.summary.note && (
        <p className="section-description" style={{ marginTop: 8 }}>ℹ️ {report.summary.note}</p>
      )}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">Equity Curve</div>
        <EquityChart points={report.equity_curve} />
      </div>

      <TailDependenceCard td={report.tail_dependence} />

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">Trades</div>
        <TradesTable report={report} />
      </div>

      <RobustnessView report={report} />

      <details style={{ marginTop: 16 }}>
        <summary className="section-description">Run manifest (reproducibility)</summary>
        <pre className="cell-mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
          {JSON.stringify(report.manifest, null, 2)}
        </pre>
      </details>
    </div>
  );
}
